import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import { Box, Card, IconButton, Stack, Typography } from '@mui/material';
import { useTranslate } from 'react-admin';
import { AccessDenied } from '@/components/shell/AccessDenied';
import { EmptyState } from '@/components/shell/EmptyState';
import { ErrorState } from '@/components/shell/ErrorState';
import { LoadingState } from '@/components/shell/LoadingState';
import { StatusChip } from '@/components/shell/StatusChip';
import { useTestBenchTimeline } from './useTestBenchTimeline';
import { isTerminalStatus } from './domain';

interface TestBenchTimelineListProps {
  intentId: string;
}

/**
 * Live timeline panel — polls `useTestBenchTimeline` every 3s and stops as
 * soon as the intent reaches a terminal status. Manual refresh always
 * available alongside the automatic polling.
 */
export function TestBenchTimelineList({ intentId }: TestBenchTimelineListProps) {
  const translate = useTranslate();
  const { timeline, loading, error, refresh } = useTestBenchTimeline(intentId);

  return (
    <Card sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {translate('pay.testBench.timeline.title')}
          </Typography>
          <IconButton size="small" onClick={refresh} aria-label={translate('pay.testBench.timeline.refreshAction')}>
            <RefreshOutlinedIcon fontSize="small" />
          </IconButton>
        </Stack>

        {loading && !timeline ? (
          <LoadingState variant="list" rows={3} />
        ) : error ? (
          error.errorCode === 'access_denied' ? (
            <AccessDenied />
          ) : (
            <ErrorState title={translate('pay.testBench.timeline.errorTitle')} body={error.message} onRetry={refresh} />
          )
        ) : !timeline || timeline.events.length === 0 ? (
          <EmptyState title={translate('pay.testBench.timeline.title')} body={translate('pay.testBench.timeline.emptyBody')} />
        ) : (
          <Stack spacing={0}>
            {timeline.events.map((event, index) => (
              <Stack
                key={`${event.type}-${event.at}-${index}`}
                direction="row"
                spacing={2}
                alignItems="baseline"
                sx={{ py: 1, borderTop: index === 0 ? undefined : '1px solid', borderColor: 'divider' }}
              >
                <Box sx={{ minWidth: 160, fontFamily: (theme) => theme.custom.fontFamilyMono, fontSize: '0.75rem', color: 'text.secondary' }}>
                  {new Date(event.at).toLocaleString('es-ES')}
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {translate(`pay.testBench.timelineEvent.${event.type}`)}
                </Typography>
                {event.detail && (
                  <Typography variant="body2" color="text.secondary">
                    {event.detail}
                  </Typography>
                )}
              </Stack>
            ))}
            {!isTerminalStatus(timeline.status) && (
              <Typography variant="caption" color="text.secondary" sx={{ pt: 1.5 }}>
                {translate('pay.testBench.timeline.pollingNotice')}
              </Typography>
            )}
            <Box sx={{ pt: 1.5 }}>
              <StatusChip
                label={translate(`pay.operations.status.${timeline.status}`)}
                tone={isTerminalStatus(timeline.status) ? (timeline.status === 'captured' ? 'success' : 'danger') : 'info'}
              />
            </Box>
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
